import type { ArcadeTheme } from './themes'

/**
 * The arcade stylesheet, generated from an `ArcadeTheme`.
 *
 * This is the pre-theme `ARCADE_CSS` with every hardcoded literal lifted to a
 * `--a-*` custom property. The STRUCTURE — the grid columns, the podium order,
 * which elements exist at all — is identical for every theme, because a theme
 * is presentation only and the markup it styles is the markup every other
 * theme styles. Only the token block and the optional per-theme overlay differ.
 *
 * Sizes are `calc(<px> * var(--a-fs))` for body text and `calc(<px> *
 * var(--a-fd))` for display text. A single px ladder cannot serve both VT323
 * (which renders tiny per em) and a proportional face like Instrument Sans, so
 * each theme scales the two ladders independently.
 */

/** The `--a-*` block for one theme. */
export function arcadeVars(t: ArcadeTheme): string {
  return `
  --a-bg: ${t.bg};
  --a-bg-solid: ${t.bgSolid};
  --a-panel: ${t.panel};
  --a-panel2: ${t.panel2};
  --a-hover: ${t.hover};
  --a-board-bg: ${t.boardBg};
  --a-veil: ${t.veil};
  --a-ink: ${t.ink};
  --a-ink-dim: ${t.inkDim};
  --a-ink-faint: ${t.inkFaint};
  --a-line: ${t.line};
  --a-line2: ${t.line2};
  --a-accent: ${t.accent};
  --a-on-accent: ${t.onAccent};
  --a-gold: ${t.gold};
  --a-cyan: ${t.cyan};
  --a-hot: ${t.hot};
  --a-violet: ${t.violet};
  --a-silver: ${t.silver};
  --a-bronze: ${t.bronze};
  --a-flame: ${t.flame};
  --a-you: ${t.youTint};
  --a-row-hover: ${t.rowHover};
  --a-spark-fill: ${t.sparkFill};
  --a-avatar-bg: ${t.avatarBg};
  --a-fd-font: ${t.fontDisplay};
  --a-fb-font: ${t.fontBody};
  --a-fs: ${t.fsScale};
  --a-fd: ${t.fdScale};
  --a-display-style: ${t.displayStyle};
  --a-display-transform: ${t.displayTransform};
  --a-display-tracking: ${t.displayTracking};
  --a-radius: ${t.radius};
  --a-radius-sm: ${t.radiusSm};
  --a-shadow: ${t.shadow};
  /* Raised surfaces (plinths, award cards, the modal) are a top-lit gradient
     in every theme, which is what the live board already does. Deriving it
     keeps the default pixel-faithful without a token per theme. */
  --a-panel-grad: linear-gradient(180deg, var(--a-panel2), var(--a-panel));
  /* Glow is a whole-theme decision, not a per-rule one: the flat, printed and
     daylight worlds set every glow slot to none in one place. */
  --a-glow-h1: ${t.glow ? '0 0 8px color-mix(in srgb, var(--a-accent) 55%, transparent), 0 0 22px color-mix(in srgb, var(--a-accent) 25%, transparent)' : 'none'};
  --a-glow-accent: ${t.glow ? '0 0 9px color-mix(in srgb, var(--a-accent) 40%, transparent)' : 'none'};
  --a-glow-gold: ${t.glow ? '0 0 8px color-mix(in srgb, var(--a-gold) 50%, transparent)' : 'none'};
  --a-glow-cyan: ${t.glow ? '0 0 9px color-mix(in srgb, var(--a-cyan) 40%, transparent)' : 'none'};
  --a-glow-box: ${t.glow ? '0 0 14px color-mix(in srgb, var(--a-accent) 45%, transparent)' : 'none'};
  --a-glow-bar: ${t.glow ? '0 0 8px var(--a-accent)' : 'none'};
  --a-modal-glow: ${t.glow ? '0 0 40px color-mix(in srgb, var(--a-accent) 15%, transparent)' : 'var(--a-shadow)'};
`
}

/** Body-text size: the px value the CRT theme was drawn at, scaled per theme. */
const b = (px: number) => `calc(${px}px * var(--a-fs))`
/** Display/label size, scaled on its own ladder. */
const d = (px: number) => `calc(${px}px * var(--a-fd))`

export function arcadeCss(t: ArcadeTheme): string {
  return `
.arcade { ${arcadeVars(t)} }
/* Normal document flow, not \`position: fixed; inset: 0\`. The fixed viewport
   fought iOS Safari's dynamic toolbar (the visual viewport shrinks but a fixed
   inset:0 box does not, so the bottom of the board sat under the browser
   chrome) and re-ran the rain canvas resize on every URL-bar show/hide.
   \`z-index: 0\` is kept deliberately: it establishes the stacking context the
   fixed rain/scanline/vignette overlays live in. */
.arcade { position: relative; min-height: 100dvh; background: var(--a-bg); color: var(--a-ink);
  font-family: var(--a-fb-font); font-size: ${b(20)}; z-index: 0; }
.arcade #rain { position: fixed; inset: 0; z-index: 0; opacity: 0.3; pointer-events: none; }
.arcade .scan { position: fixed; inset: 0; z-index: 2; pointer-events: none;
  background: repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0 2px, rgba(0,0,0,0.28) 3px 4px); mix-blend-mode: multiply; }
.arcade .vig { position: fixed; inset: 0; z-index: 3; pointer-events: none;
  background: radial-gradient(120% 90% at 50% 40%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.72) 100%); }
.arcade .wrap { position: relative; z-index: 4; max-width: 1180px; margin: 0 auto; padding: 26px 20px 60px; }
.arcade header.top { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 16px;
  border-bottom: 2px solid var(--a-line); padding-bottom: 16px; }
.arcade .brand { display: flex; flex-direction: column; gap: 18px; min-width: 0; }
.arcade .brandrow { display: flex; align-items: center; gap: 22px; }
.arcade .pixlogo { width: 52px; height: 52px; }
.arcade .headright { display: flex; flex-direction: column; align-items: flex-end; gap: 12px; }
/* Opens the header sheet on a phone. Hidden above the mobile breakpoint —
   there is ONE control cluster and one markup tree; only its presentation
   changes, so a phone-only tree can never drift from the desktop one. */
.arcade .menubtn { display: none; font-family: var(--a-fd-font); font-size: ${d(9)}; color: var(--a-ink-dim);
  background: var(--a-panel); border: 1px solid var(--a-line); border-radius: var(--a-radius-sm);
  align-items: center; gap: 6px; padding: 0 12px; min-height: 44px; cursor: pointer; }
.arcade .menubtn:hover { color: var(--a-accent); border-color: var(--a-ink-faint); }
.arcade .eyebrow { font-family: var(--a-fd-font); font-size: ${d(10)}; letter-spacing: 2px; color: var(--a-ink-faint);
  text-transform: var(--a-display-transform); }
.arcade h1 { font-family: var(--a-fd-font); font-size: calc(clamp(20px,4.4vw,38px) * var(--a-fd)); margin: 12px 0 10px; color: var(--a-accent);
  font-style: var(--a-display-style); text-transform: var(--a-display-transform); letter-spacing: var(--a-display-tracking);
  text-shadow: var(--a-glow-h1); }
.arcade h1 .s { color: var(--a-gold); text-shadow: var(--a-glow-gold); }
.arcade .coin { font-family: var(--a-fd-font); font-size: ${d(10)}; color: var(--a-gold); }
.arcade .blink { animation: abl 1.1s steps(1) infinite; }
@keyframes abl { 50% { opacity: 0; } }
.arcade .credits { text-align: right; font-size: ${b(18)}; color: var(--a-ink-dim); }
.arcade .credits b { color: var(--a-accent); }
.arcade .topright { display: flex; gap: 10px; align-items: center; margin-bottom: 6px; flex-wrap: wrap; justify-content: flex-end; }
/* Round 13 design review, finding 3: '.glink'/'.tab'/'.win' used to be a
   bordered, uppercase, --a-radius-sm rectangle -- a second button system
   from the true pill 'pillButtonStyle' (pages/StaffPortal.tsx) draws for
   Home's own tab bar and window picker, one screen away on the SAME arcade
   host. That split had to work across ten very different cabinet
   aesthetics, some of which wanted the sharper bordered look; Bento HQ is
   the only theme left, so these now match the pill exactly: full
   border-radius 999px, no border, no uppercase. The '.themepick' rules
   that used to sit here were deleted with the cabinet picker itself
   (round 13) rather than left as dead CSS for a selector nothing renders. */
.arcade .glink { font-family: var(--a-fb-font); font-size: ${b(13)}; font-weight: 600; color: var(--a-ink-dim); background: transparent;
  border: none; padding: 9px 14px; cursor: pointer; text-decoration: none; border-radius: 999px; }
.arcade .glink:hover { color: var(--a-accent); }
.arcade .controls { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 14px; margin: 20px 0 6px; }
.arcade .modes { display: flex; flex-wrap: wrap; gap: 8px; }
.arcade .tab { font-family: var(--a-fb-font); font-size: ${b(13)}; font-weight: 600; color: var(--a-ink-dim); background: transparent;
  border: none; padding: 10px 16px; cursor: pointer; border-radius: 999px; }
.arcade .tab:hover { color: var(--a-accent); }
.arcade .tab[data-on="1"] { color: var(--a-on-accent); background: var(--a-accent); box-shadow: var(--a-glow-box); }
.arcade .tab.tok[data-on="1"] { background: var(--a-cyan); box-shadow: var(--a-glow-cyan); }
.arcade .win { font-family: var(--a-fb-font); font-size: ${b(12)}; font-weight: 600; color: var(--a-ink-dim); background: transparent;
  border: none; padding: 8px 14px; cursor: pointer; border-radius: 999px; }
.arcade .win[data-on="1"] { color: var(--a-on-accent); background: var(--a-gold); }
.arcade .wins { display: flex; gap: 6px; align-items: center; }
/* Two labelled groups. Rolling (1H-24H) and calendar (DAY/WK/MO/ALL) are not
   one scale -- DAY is a Sydney calendar day, not the last 24 hours -- so they
   are separated visually as well as in the markup. */
.arcade .wingroups { display: flex; gap: 14px; align-items: center; flex-wrap: wrap; }
.arcade .winlab { font-family: var(--a-fd-font); font-size: ${d(8)}; letter-spacing: .11em;
  text-transform: uppercase; color: var(--a-ink-faint); }
/* THE SECOND LEVEL of navigation, inside one mode. Deliberately quieter than
   .tab: the top-level modes are the board's identity and win the contrast, and
   a second row of filled pills would read as two competing navigations. This
   is an underline strip -- present, obviously interactive, subordinate. It
   scrolls sideways rather than wrapping, so a seven-tab view keeps one row and
   the content below it does not move down a line when the window narrows. */
.arcade .subtabs { display: flex; gap: 2px; margin: 0 0 16px; overflow-x: auto;
  scrollbar-width: none; border-bottom: 1px solid var(--a-line); }
.arcade .subtabs::-webkit-scrollbar { display: none; }
.arcade .subtabs button { font-family: var(--a-fd-font); font-size: ${d(9)}; color: var(--a-ink-faint);
  background: transparent; border: none; border-bottom: 2px solid transparent; padding: 11px 14px;
  cursor: pointer; white-space: nowrap; text-transform: uppercase;
  letter-spacing: var(--a-display-tracking); min-height: 44px; }
.arcade .subtabs button:hover { color: var(--a-ink-dim); }
.arcade .subtabs button[data-on="1"] { color: var(--a-accent); border-bottom-color: var(--a-accent); }
/* The strip is ONE tab stop (roving tabindex), so the focus ring has to be
   unmissable on the element that actually has focus. */
.arcade .subtabs button:focus-visible { outline: 2px solid var(--a-accent); outline-offset: -2px; }
/* The panel is focusable so a keyboard user can move from the strip into the
   content, but it is not a control and must not draw a box of its own. */
.arcade [role="tabpanel"]:focus { outline: none; }
.arcade [role="tabpanel"]:focus-visible { outline: 2px solid var(--a-accent); outline-offset: 4px; }
.arcade .hint { font-size: ${b(19)}; color: var(--a-ink-dim); margin: 6px 0 18px; }
.arcade .hint b { color: var(--a-accent); }
.arcade .podium { display: grid; grid-template-columns: 1fr 1.15fr 1fr; gap: 12px; align-items: end; margin-bottom: 24px; }
/* The champion sits centre on a three-across podium, which needs 2/1/3. That
   arrangement used to be an INLINE \`order\`, so it survived the single-column
   collapse and every phone showed the runner-up above the winner. Reading it
   from a custom property lets the mobile query put the podium back into source
   order (1st, 2nd, 3rd), which is the only order that makes sense stacked. */
.arcade .plinth { order: var(--ord, 0); background: var(--a-panel-grad); border: 1px solid var(--a-line);
  border-top: 3px solid var(--pos); padding: 16px 12px; text-align: center; border-radius: var(--a-radius); }
.arcade .plinth .medal { font-family: var(--a-fd-font); font-size: ${d(12)}; color: var(--pos); }
.arcade .plinth .podav { display: block; margin: 12px auto 4px; border-color: var(--pos);
  box-shadow: 0 0 12px -2px var(--pos); transform-origin: center bottom; }
/* Idle animation for the top three only, each with its own tempo so the row
   feels alive rather than metronomic. Gold bobs proudly, silver/bronze sway. */
.arcade .plinth .podav-1 { animation: podbob 2.4s ease-in-out infinite; }
.arcade .plinth .podav-2 { animation: podsway 3.1s ease-in-out infinite; }
.arcade .plinth .podav-3 { animation: podsway 3.6s ease-in-out infinite .3s; }
@keyframes podbob {
  0%,100% { transform: translateY(0) scale(1); box-shadow: 0 0 12px -2px var(--pos); }
  50% { transform: translateY(-6px) scale(1.04); box-shadow: 0 0 20px 0 var(--pos); }
}
@keyframes podsway {
  0%,100% { transform: translateY(0) rotate(-2deg); }
  50% { transform: translateY(-3px) rotate(2deg); }
}
.arcade .plinth .who { font-size: ${b(26)}; color: var(--a-ink); margin-top: 8px; line-height: 1; }
.arcade .plinth .cls { font-size: ${b(17)}; color: var(--a-ink-dim); }
.arcade .plinth .score { font-family: var(--a-fd-font); font-size: ${d(15)}; color: var(--pos); margin-top: 12px; text-shadow: var(--a-glow-accent); }
.arcade .layout { display: grid; grid-template-columns: minmax(0,1fr) 300px; gap: 16px; align-items: start; }
.arcade .layout > div:first-child { min-width: 0; }
.arcade .board { border: 1px solid var(--a-line); background: var(--a-board-bg); overflow: hidden; border-radius: var(--a-radius); }
.arcade .row { display: grid; grid-template-columns: 44px minmax(0,1fr) 132px 116px 128px; align-items: center; gap: 14px;
  padding: 12px 16px; border-bottom: 1px solid var(--a-line2); }
.arcade .row.head { color: var(--a-ink-faint); font-family: var(--a-fd-font); font-size: ${d(9)}; padding: 14px 16px;
  border-bottom: 2px solid var(--a-line); background: var(--a-panel); }
.arcade .row.you { background: var(--a-you); box-shadow: inset 3px 0 0 var(--a-accent); }
.arcade .row .who { display: flex; gap: 12px; align-items: center; min-width: 0; }
.arcade .rank { font-family: var(--a-fd-font); font-size: ${d(15)}; color: var(--a-ink-dim); }
.arcade .rank.top3 { color: var(--a-gold); text-shadow: var(--a-glow-gold); }
.arcade .nm { font-size: ${b(23)}; color: var(--a-ink); line-height: 1; }
.arcade .badges { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 3px; }
.arcade .bdg { font-family: var(--a-fd-font); font-size: ${d(7)}; padding: 4px 5px; border: 1px solid var(--a-line); color: var(--a-ink-faint); white-space: nowrap; }
.arcade .bdg.hot { color: var(--a-hot); border-color: var(--a-line); border-color: color-mix(in srgb, var(--a-hot) 35%, var(--a-line)); }
.arcade .bdg.gold { color: var(--a-gold); border-color: var(--a-line); border-color: color-mix(in srgb, var(--a-gold) 35%, var(--a-line)); }
.arcade .clsC { font-size: ${b(19)}; color: var(--a-cyan); }
.arcade .clsC .role { color: var(--a-ink-dim); font-size: ${b(15)}; display: block; }

/* The rank-move marker. ONE rule for all seven layouts, because the arrow
   pair is a reserved arcade-wide vocabulary item (see the Mover atom) and a
   per-skeleton restyle is how it would stop reading as one thing. It sits
   under the rank in the column layouts and beside it in the terminal table.
   Themed through --a-* like everything else, so an up-move is the theme's
   accent and a down-move its hot, never a literal green/red that would clash
   in nine palettes out of ten. */
.arcade .mover { font-family: var(--a-fb-font); font-size: ${b(14)}; letter-spacing: 0.5px;
  font-variant-numeric: tabular-nums; white-space: nowrap; }
.arcade .rank .mover { display: block; margin-top: 2px; }
.arcade .rk .mover, .arcade .pos .mover, .arcade .flaprank .mover { display: block; margin-top: 2px; }
.arcade .motd .mrow .mover { display: inline-block; margin-left: 6px; }
.arcade .mover.up { color: var(--a-accent); }
.arcade .mover.down { color: var(--a-hot); }
.arcade .sc { font-family: var(--a-fd-font); font-size: ${d(16)}; color: var(--a-accent); text-align: right;
  text-shadow: var(--a-glow-accent); font-variant-numeric: tabular-nums; }
.arcade .sc.tok { color: var(--a-cyan); text-shadow: var(--a-glow-cyan); }
/* THE SURFACE BREAKDOWN under a score: chat versus Claude Code.
   Deliberately quiet and deliberately BELOW the figure. It is a breakdown of
   the number above it, so it must never compete with that number for the eye
   and must never look like a second amount sitting beside it — which is
   exactly how the "+ $12.00 key" slot it replaced read. No plus sign is
   possible here: the separator is the same middot the sub-lines already use.
   Both halves are dimmed ink; CHAT is not accented and CODE is not accented,
   because tinting one surface would make it look like the good one, and this
   board takes no position on which door somebody came through. */
.arcade .surfsplit { display: block; margin-top: 3px; font-family: var(--a-fb-font);
  font-size: ${b(13)}; color: var(--a-ink-dim); letter-spacing: 0; text-shadow: none;
  font-variant-numeric: tabular-nums; white-space: nowrap; }
.arcade .surfsplit .sf i { font-style: normal; color: var(--a-ink-faint); letter-spacing: .06em; }
.arcade .surfsplit .sf em { font-style: normal; color: var(--a-ink-faint); margin-left: 4px; }
.arcade .surfsplit .sfsep { color: var(--a-ink-faint); margin: 0 5px; }
/* The board's one honest sentence when the split could not be computed. Left
   aligned and full width because it is a statement about the BOARD, not a
   value in any row's column. */
.arcade .splitnote { font-size: ${b(15)}; color: var(--a-ink-faint); line-height: 1.45;
  margin: 8px 0 0; text-align: left; }
.arcade .lvl .lab { font-size: ${b(15)}; color: var(--a-ink-dim); }
.arcade .lvl .track { height: 8px; background: var(--a-line2); margin-top: 4px; border: 1px solid var(--a-line); }
.arcade .lvl .fill { height: 100%; background: var(--a-accent); box-shadow: var(--a-glow-bar); transition: width .9s cubic-bezier(.2,.7,.2,1); }
.arcade .teams { display: flex; flex-direction: column; gap: 10px; }
.arcade .team { border: 1px solid var(--a-line); background: var(--a-panel); padding: 12px 16px; border-radius: var(--a-radius); }
.arcade .team .tl { display: flex; justify-content: space-between; align-items: baseline; }
.arcade .team .fac { font-family: var(--a-fd-font); font-size: ${d(11)}; color: var(--a-ink); }
.arcade .team .amt { font-family: var(--a-fd-font); font-size: ${d(12)}; color: var(--pos); }
.arcade .team .track { height: 14px; background: var(--a-line2); margin-top: 10px; border: 1px solid var(--a-line); }
.arcade .team .fill { height: 100%; background: var(--pos); box-shadow: 0 0 10px var(--pos); transition: width 1s cubic-bezier(.2,.7,.2,1); }
.arcade .pcard { border: 1px solid var(--a-line); background: var(--a-panel-grad); padding: 18px 16px; position: sticky; top: 16px; border-radius: var(--a-radius); }
.arcade .pcard .tag { font-family: var(--a-fd-font); font-size: ${d(9)}; color: var(--a-gold); }
.arcade .pcard .pn { font-size: ${b(30)}; color: var(--a-accent); margin: 10px 0 2px; text-shadow: var(--a-glow-accent); }
.arcade .pcard .pr { font-family: var(--a-fd-font); font-size: ${d(10)}; color: var(--a-cyan); }
.arcade .pcard .st { display: flex; justify-content: space-between; border-top: 1px solid var(--a-line2); padding: 10px 0; font-size: ${b(20)}; color: var(--a-ink-dim); }
.arcade .pcard .st b { color: var(--a-ink); font-variant-numeric: tabular-nums; }
.arcade .awards { display: flex; flex-direction: column; gap: 12px; position: sticky; top: 16px; }
.arcade .acard { border: 1px solid var(--a-line); border-top: 3px solid var(--ac); background: var(--a-panel-grad); padding: 14px; border-radius: var(--a-radius); }
.arcade .acard .atitle { font-family: var(--a-fd-font); font-size: ${d(9)}; color: var(--ac); }
.arcade .acard .aname { font-size: ${b(24)}; color: var(--ac); margin: 9px 0 1px; line-height: 1; }
.arcade .acard .alabel { font-size: ${b(15)}; color: var(--a-ink-dim); }
.arcade .acard .astats { display: flex; gap: 14px; margin-top: 10px; flex-wrap: wrap; }
.arcade .acard .astat { font-size: ${b(13)}; color: var(--a-ink-dim); }
.arcade .acard .astat b { color: var(--a-ink); display: block; font-size: ${b(17)}; font-variant-numeric: tabular-nums; }
.arcade .mover { display: block; font-family: var(--a-fb-font); font-size: ${b(14)}; margin-top: 2px; letter-spacing: 0.5px; }
.arcade .flame { color: var(--a-flame); border-color: var(--a-line); border-color: color-mix(in srgb, var(--a-flame) 35%, var(--a-line)); }
.arcade .team .mvp { font-size: ${b(15)}; color: var(--a-ink-dim); margin-top: 8px; }
.arcade .team .mvp b { color: var(--a-ink); }
.arcade .team.clickable { cursor: pointer; transition: border-color .15s, background .15s; }
.arcade .team.clickable:hover, .arcade .team.clickable:focus-visible { border-color: var(--a-ink-faint); background: var(--a-hover); outline: none; }
.arcade .team .drill { font-family: var(--a-fd-font); font-size: ${d(8)}; color: var(--a-ink-faint); margin-top: 10px; text-align: right; }
.arcade .team.clickable:hover .drill, .arcade .team.clickable:focus-visible .drill { color: var(--a-accent); }
.arcade .teamdetail .back { font-family: var(--a-fd-font); font-size: ${d(9)}; color: var(--a-ink-dim); background: transparent;
  border: 1px solid var(--a-line); padding: 10px 12px; cursor: pointer; margin-bottom: 18px; border-radius: var(--a-radius-sm); }
.arcade .teamdetail .back:hover { color: var(--a-accent); border-color: var(--a-ink-faint); }
.arcade .teamdetail h2 { font-family: var(--a-fd-font); font-size: ${d(18)}; color: var(--a-ink); margin: 0 0 6px;
  font-style: var(--a-display-style); letter-spacing: var(--a-display-tracking); }
.arcade .teamdetail .sub { font-size: ${b(17)}; color: var(--a-ink-dim); margin-bottom: 20px; }
.arcade .members { display: grid; grid-template-columns: repeat(auto-fill,minmax(230px,1fr)); gap: 12px; margin-bottom: 28px; }
.arcade .member { border: 1px solid var(--a-line); background: var(--a-panel); padding: 12px; display: flex; gap: 10px; align-items: flex-start; border-radius: var(--a-radius); }
.arcade .member.you { box-shadow: inset 3px 0 0 var(--a-accent); background: var(--a-you); }
.arcade .member .mname { font-size: ${b(20)}; color: var(--a-ink); line-height: 1.1; }
.arcade .member .mcls { font-size: ${b(14)}; color: var(--a-cyan); margin-top: 1px; }
.arcade .member .mstats { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 7px; font-size: ${b(13)}; color: var(--a-ink-dim); }
.arcade .member .mstats b { color: var(--a-ink); font-variant-numeric: tabular-nums; }
.arcade .taxhead { font-family: var(--a-fd-font); font-size: ${d(11)}; color: var(--a-gold); margin: 6px 0 12px; }
.arcade .spotlight { border: 1px solid var(--a-line); border-left: 3px solid var(--a-accent); background: var(--a-you);
  padding: 12px 16px; margin-bottom: 20px; font-size: ${b(20)}; color: var(--a-ink); border-radius: var(--a-radius); }
.arcade .spotlight b { color: var(--a-accent); font-family: var(--a-fd-font); font-size: ${d(12)}; }
.arcade .spotlight .youchase { color: var(--a-ink); }
.arcade .spotlight .youscore { margin-left: 10px; font-family: var(--a-fd-font); font-size: ${d(11)};
  color: var(--a-cyan); font-variant-numeric: tabular-nums; }
.arcade .foot { margin-top: 22px; font-size: ${b(17)}; color: var(--a-ink-dim); text-align: center; line-height: 1.5; }
/* Row/plinth/member drill-in affordance — a player is clickable everywhere
   they appear (podium, board, team roster, topic contributors). */
.arcade .row.clickable, .arcade .plinth.clickable, .arcade .member.clickable { cursor: pointer; transition: border-color .15s, background .15s; }
.arcade .row.clickable:hover, .arcade .row.clickable:focus-visible { background: var(--a-row-hover); outline: none; }
.arcade .row.you.clickable:hover, .arcade .row.you.clickable:focus-visible { background: var(--a-you); }
.arcade .plinth.clickable:hover, .arcade .plinth.clickable:focus-visible { filter: brightness(1.12); outline: none; }
.arcade .member.clickable:hover, .arcade .member.clickable:focus-visible { border-color: var(--a-ink-faint); background: var(--a-hover); outline: none; }
/* Topic contributor / model chips (Taxonomy topic drill-down). */
.arcade .contributors { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px; }
.arcade .chip { display: inline-flex; align-items: center; gap: 8px; border: 1px solid var(--a-line); background: var(--a-panel);
  color: var(--a-ink); font-family: var(--a-fb-font); font-size: ${b(16)}; padding: 6px 10px; cursor: pointer; border-radius: var(--a-radius-sm); }
.arcade .chip:hover, .arcade .chip:focus-visible { border-color: var(--a-ink-faint); background: var(--a-hover); outline: none; }
.arcade .chip.static { cursor: default; }
.arcade .chip.static:hover { border-color: var(--a-line); background: var(--a-panel); }
.arcade .chip .cname { white-space: nowrap; }
.arcade .chip .ccount { font-family: var(--a-fd-font); font-size: ${d(9)}; color: var(--a-gold); }
/* Profile drill-down modal. */
.arcade .modalveil { position: fixed; inset: 0; z-index: 10; background: var(--a-veil);
  display: flex; align-items: flex-start; justify-content: center; overflow: auto; padding: 40px 16px; }
.arcade .modal { position: relative; z-index: 11; width: 100%; max-width: 560px; background: var(--a-panel-grad);
  border: 1px solid var(--a-ink-faint); box-shadow: var(--a-modal-glow); padding: 22px 24px 26px; border-radius: var(--a-radius); }
.arcade .modal h2 { font-family: var(--a-fb-font); font-size: ${b(30)}; color: var(--a-accent); text-shadow: var(--a-glow-accent); }
.arcade .youtag { font-family: var(--a-fd-font); font-size: ${d(8)}; color: var(--a-on-accent); background: var(--a-accent);
  padding: 3px 6px; margin-left: 10px; vertical-align: middle; }
.arcade .pstats { display: grid; grid-template-columns: repeat(3,1fr); gap: 12px; margin: 18px 0 6px; }
.arcade .pstat { border: 1px solid var(--a-line); background: var(--a-panel); padding: 8px 10px; border-radius: var(--a-radius-sm); }
.arcade .pstat .plab { display: block; font-family: var(--a-fd-font); font-size: ${d(8)}; color: var(--a-ink-dim); margin-bottom: 4px; }
.arcade .pstat b { font-size: ${b(19)}; color: var(--a-ink); font-variant-numeric: tabular-nums; }
.arcade .sharerow { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.arcade .sharerow button:disabled { opacity: .5; cursor: wait; }
.arcade .sharestatus { font-family: var(--a-fd-font); font-size: ${d(9)}; color: var(--a-accent); }
/* ---------------------------------------------------------------------------
   SLOT: ticker (Trading Floor).
   Structure, not ornament, so it lives here rather than in a theme overlay —
   any theme may switch it on. The tape is duplicated in the markup and the
   translate loops at -50%, which is what makes the scroll seamless.
   --------------------------------------------------------------------------- */
.arcade .tickerbar { border-top: 1px solid var(--a-line); border-bottom: 1px solid var(--a-line);
  background: var(--a-panel); overflow: hidden; margin-bottom: 18px; padding: 8px 0; }
.arcade .tickerbar .tapes { display: flex; width: max-content; animation: atick 38s linear infinite; }
.arcade .tickerbar .tape { display: flex; gap: 30px; padding-right: 30px; }
.arcade .tickerbar .tick { font-family: var(--a-fd-font); font-size: ${d(10)}; color: var(--a-ink-dim);
  white-space: nowrap; letter-spacing: .06em; }
.arcade .tickerbar .tick b { color: var(--a-accent); font-variant-numeric: tabular-nums; }
@keyframes atick { from { transform: translateX(0); } to { transform: translateX(-50%); } }

/* ---------------------------------------------------------------------------
   SKELETON: ladder (Season Ladder).
   The same ranked array partitioned into divisions with a promotion cut-line.
   The bottom band is a development league: listed, but never ranked.
   --------------------------------------------------------------------------- */
.arcade .ladder { display: grid; grid-template-columns: minmax(0,1fr) 290px; gap: 16px; align-items: start; }
.arcade .ladder .divs { display: flex; flex-direction: column; gap: 14px; min-width: 0; }
.arcade .ladder .div { border: 1px solid var(--a-line); background: var(--a-board-bg);
  border-radius: var(--a-radius); padding: 12px 14px; }
.arcade .ladder .div.gold { border-color: color-mix(in srgb, var(--a-gold) 45%, var(--a-line));
  background: linear-gradient(180deg, color-mix(in srgb, var(--a-gold) 10%, transparent), transparent 60%), var(--a-board-bg); }
.arcade .ladder .div h4 { display: flex; align-items: center; gap: 8px; margin: 0 0 10px;
  font-family: var(--a-fd-font); font-size: ${d(12)}; color: var(--a-ink); font-weight: normal;
  text-transform: var(--a-display-transform); letter-spacing: var(--a-display-tracking); }
.arcade .ladder .div .bd { width: 10px; height: 10px; border-radius: 50%; background: var(--a-gold); flex: none; }
.arcade .ladder .div.silver .bd { background: var(--a-silver); }
.arcade .ladder .dcount { margin-left: auto; font-size: ${d(9)}; color: var(--a-ink-faint); }
.arcade .ladder .lrow { display: flex; align-items: center; gap: 10px; padding: 8px 6px;
  border-bottom: 1px solid var(--a-line2); }
.arcade .ladder .lrow:last-of-type { border-bottom: 0; }
.arcade .ladder .lrow.you { background: var(--a-you); box-shadow: inset 3px 0 0 var(--a-accent); }
.arcade .ladder .lrow .rk { font-family: var(--a-fd-font); font-size: ${d(12)}; color: var(--a-ink-dim);
  width: 24px; flex: none; font-variant-numeric: tabular-nums; }
.arcade .ladder .lrow .nm { font-size: ${b(21)}; color: var(--a-ink); line-height: 1.1;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.arcade .ladder .lrow .sub { font-size: ${b(15)}; color: var(--a-ink-dim); overflow: hidden;
  text-overflow: ellipsis; white-space: nowrap; }
.arcade .ladder .lrow .pts { font-family: var(--a-fd-font); font-size: ${d(12)}; color: var(--a-gold);
  flex: none; font-variant-numeric: tabular-nums; text-align: right; }
.arcade .ladder .cut { border-top: 1px dashed var(--a-accent); color: var(--a-accent);
  font-family: var(--a-fd-font); font-size: ${d(9)}; padding-top: 8px; margin-top: 8px; letter-spacing: .06em; }
.arcade .ladderside { display: flex; flex-direction: column; gap: 12px; position: sticky; top: 16px; }
.arcade .lcard { border: 1px solid var(--a-line); background: var(--a-panel-grad); padding: 14px;
  border-radius: var(--a-radius); }
.arcade .lcard .k { font-family: var(--a-fd-font); font-size: ${d(10)}; color: var(--a-gold); margin-bottom: 10px; }
.arcade .devnote { font-size: ${b(17)}; color: var(--a-ink-dim); line-height: 1.45; margin-bottom: 10px; }
.arcade .devlist { display: flex; flex-wrap: wrap; gap: 6px; }
.arcade .lstat { display: flex; justify-content: space-between; font-size: ${b(18)}; color: var(--a-ink-dim);
  padding: 5px 0; border-top: 1px solid var(--a-line2); }
.arcade .lstat b { color: var(--a-ink); font-variant-numeric: tabular-nums; }
.arcade .chip.you { border-color: var(--a-accent); }

/* ---------------------------------------------------------------------------
   SKELETON: lanes (Grand Prix).
   Position is tokens as a fraction of the leader's, so the board shows the gap
   rather than an ordinal. The chequered wall is the 100% mark.
   --------------------------------------------------------------------------- */
.arcade .gp .track { display: flex; flex-direction: column; gap: 7px; margin-bottom: 18px; }
.arcade .gp .lane { position: relative; height: 44px; background: var(--a-panel); border-radius: var(--a-radius-sm);
  display: flex; align-items: center; cursor: pointer; border: 1px solid var(--a-line2); }
.arcade .gp .lane:hover, .arcade .gp .lane:focus-visible { border-color: var(--a-ink-faint); outline: none; }
.arcade .gp .lane.you { box-shadow: inset 3px 0 0 var(--a-accent); background: var(--a-you); }
.arcade .gp .lane::after { content: ""; position: absolute; right: 0; top: 0; bottom: 0; width: 16px;
  background-image: repeating-conic-gradient(var(--a-ink) 0 25%, var(--a-panel2) 0 50%);
  background-size: 8px 8px; border-radius: 0 var(--a-radius-sm) var(--a-radius-sm) 0; }
.arcade .gp .pos { position: absolute; left: 10px; font-family: var(--a-fd-font); font-size: ${d(9)};
  color: var(--a-ink-faint); }
.arcade .gp .car { position: absolute; left: var(--x); transform: translateX(-100%); display: flex;
  align-items: center; gap: 8px; padding-right: 8px; max-width: calc(100% - 20px);
  transition: left .9s cubic-bezier(.2,.7,.2,1); }
.arcade .gp .no { width: 26px; height: 26px; border-radius: var(--a-radius-sm); background: var(--a-accent);
  color: var(--a-on-accent); font-family: var(--a-fd-font); font-size: ${d(11)}; display: grid;
  place-items: center; flex: none; }
.arcade .gp .cn { font-size: ${b(18)}; color: var(--a-ink); white-space: nowrap; overflow: hidden;
  text-overflow: ellipsis; }
.arcade .gp .cn b { font-family: var(--a-fd-font); font-size: ${d(10)}; color: var(--a-cyan);
  font-variant-numeric: tabular-nums; }
.arcade .gp .grid { margin-bottom: 18px; }
.arcade .gp .grid .k, .arcade .gp .mini .k { font-family: var(--a-fd-font); font-size: ${d(10)};
  color: var(--a-gold); margin-bottom: 8px; }
.arcade .gp .gridlist { display: flex; flex-wrap: wrap; gap: 6px; }
.arcade .gp .gpos { font-family: var(--a-fd-font); font-size: ${d(9)}; color: var(--a-ink-faint); }
.arcade .gp .pits { display: grid; grid-template-columns: repeat(3,1fr); gap: 12px; }
.arcade .gp .mini { border: 1px solid var(--a-line); background: var(--a-panel); padding: 12px;
  border-radius: var(--a-radius); }
.arcade .gp .fl { display: flex; justify-content: space-between; gap: 10px; font-size: ${b(18)};
  color: var(--a-ink-dim); padding: 3px 0; }
.arcade .gp .fl b { color: var(--a-accent); font-family: var(--a-fd-font); font-size: ${d(10)};
  font-variant-numeric: tabular-nums; }

/* ---------------------------------------------------------------------------
   SKELETON: bento (Bento HQ) — the built-in default.
   The five ranking modes become simultaneous panels instead of a tab bar.
   --------------------------------------------------------------------------- */
/* THE LAYOUT PROBLEM THIS GRID SOLVES. Until v0.20.0 the board card spanned
   three grid rows and was capped at 72vh while the five cards beside it were
   content-sized, so the board was always taller than its neighbour and the
   right-hand columns trailed off into several hundred pixels of nothing.
   Stretching the right column would only have stretched the emptiness. The
   fix is composition, not height: a full-width stat strip absorbs the loose
   KPIs, the right region carries three panels of comparable weight instead of
   five stubs, and a full-width base row gives the two usage lists a home.
   \`align-content: start\` + \`grid-auto-rows: min-content\` still stop the tall
   board setting the page height; the roster scrolls inside its own card. */
.arcade .bento { display: grid; grid-template-columns: minmax(0,1.45fr) minmax(0,1fr) minmax(0,1fr);
  gap: 12px; align-items: start; align-content: start; grid-auto-rows: min-content; }
.arcade .bcard { border: 1px solid var(--a-line); background: var(--a-panel); border-radius: var(--a-radius);
  padding: 14px 16px; min-width: 0; }
.arcade .bcard .k { font-family: var(--a-fd-font); font-size: ${d(9)}; letter-spacing: .1em;
  text-transform: uppercase; color: var(--a-ink-faint); margin-bottom: 10px;
  display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.arcade .bcard .k .more { font-family: var(--a-fb-font); font-size: ${b(14)}; letter-spacing: 0;
  text-transform: none; color: var(--a-accent); flex: none; }

/* ZONE 1 — the stat strip, across all three columns. */
.arcade .bstrip { grid-column: 1 / -1; display: grid; grid-template-columns: repeat(5, minmax(0,1fr));
  gap: 12px; }
.arcade .bstat { border: 1px solid var(--a-line); background: var(--a-panel);
  border-radius: var(--a-radius); padding: 12px 14px; display: flex; flex-direction: column;
  gap: 2px; min-width: 0; }
.arcade .bstat .sl { font-family: var(--a-fd-font); font-size: ${d(8)}; letter-spacing: .1em;
  text-transform: uppercase; color: var(--a-ink-faint); }
.arcade .bstat .sv { font-family: var(--a-fd-font); font-size: ${d(24)}; color: var(--a-ink);
  line-height: 1.1; font-variant-numeric: tabular-nums; }
.arcade .bstat .ss { font-size: ${b(15)}; color: var(--a-ink-dim); line-height: 1.35; }
.arcade .bstat.accent { background: linear-gradient(150deg, var(--a-accent),
  color-mix(in srgb, var(--a-accent) 55%, var(--a-bg-solid))); border-color: var(--a-accent); }
.arcade .bstat.accent .sl, .arcade .bstat.accent .sv { color: var(--a-on-accent); }
.arcade .bstat.accent .ss { color: var(--a-on-accent); opacity: .9; }

/* ZONE 2 — board left, three panels right. The board spans the rows the right
   stack actually occupies, rather than a hardcoded three. */
.arcade .blead { grid-row: span 4; max-height: min(72vh, 720px); display: flex; flex-direction: column; }
.arcade .blist { display: flex; flex-direction: column; overflow-y: auto; min-height: 0; }
.arcade .brow { display: flex; align-items: center; gap: 10px; padding: 7px 4px;
  border-bottom: 1px solid var(--a-line2); }
.arcade .brow:last-child { border-bottom: 0; }
.arcade .brow.you { background: var(--a-you); border-radius: 0;
  box-shadow: inset 3px 0 0 var(--a-accent); }
.arcade .brow .rk { font-family: var(--a-fd-font); font-size: ${d(11)}; color: var(--a-ink-dim);
  width: 20px; flex: none; font-variant-numeric: tabular-nums; }
.arcade .brow .rk.top3 { color: var(--a-gold); }
.arcade .brow .nm { font-size: ${b(21)}; color: var(--a-ink); line-height: 1.15; overflow: hidden;
  text-overflow: ellipsis; white-space: nowrap; }
.arcade .brow .youmark { color: var(--a-accent); font-family: var(--a-fd-font); font-size: ${d(8)}; }
.arcade .brow .sub { font-size: ${b(15)}; color: var(--a-ink-dim); overflow: hidden;
  text-overflow: ellipsis; white-space: nowrap; }
.arcade .brow .val { font-family: var(--a-fd-font); font-size: ${d(11)}; color: var(--a-accent);
  flex: none; font-variant-numeric: tabular-nums; text-align: right; }

/* The development league, on the front page. Spans both right columns: it is
   the second-largest panel here on purpose. */
.arcade .bdev { grid-column: 2 / -1; }
.arcade .bdev .devnote { font-size: ${b(17)}; color: var(--a-ink-faint); line-height: 1.45;
  margin: 0 0 10px; }
.arcade .bdev .devnote b { color: var(--a-ink); }
.arcade .bdev .devgrid { display: flex; flex-wrap: wrap; gap: 6px; }
.arcade .bdev .devchip { display: inline-flex; align-items: center; gap: 7px; cursor: pointer;
  border: 1px solid var(--a-line); background: var(--a-panel2); color: var(--a-ink);
  border-radius: 999px; padding: 3px 11px 3px 3px; font: inherit; font-size: ${b(16)}; }
.arcade .bdev .devchip:hover { background: var(--a-hover); }
.arcade .bdev .devchip.you { border-color: var(--a-accent); }

/* The two rivalry teasers. Both sides keep full saturation whatever the score:
   nothing here dims, reddens or shrinks the trailing group. */
.arcade .bvs .vsmini { display: flex; flex-direction: column; gap: 8px; }
.arcade .bvs .vsnames { display: flex; justify-content: space-between; font-size: ${b(16)};
  font-family: var(--a-fd-font); }
.arcade .bvs .sidea { color: var(--a-flame); }
.arcade .bvs .sideb { color: var(--a-cyan); }
.arcade .bvs .vsrow { display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 9px; }
.arcade .bvs .vsbar { height: 9px; background: var(--a-line2); border-radius: 3px; overflow: hidden;
  position: relative; }
.arcade .bvs .vsbar i { position: absolute; top: 0; bottom: 0; display: block; border-radius: 3px;
  transition: width .9s cubic-bezier(.2,.7,.2,1); }
.arcade .bvs .vsbar.l i { right: 0; background: var(--a-flame); }
.arcade .bvs .vsbar.r i { left: 0; background: var(--a-cyan); }
.arcade .bvs .vsmid { font-size: ${b(14)}; color: var(--a-ink-dim); white-space: nowrap;
  font-variant-numeric: tabular-nums; }
.arcade .bvs .vsnote { font-size: ${b(15)}; color: var(--a-ink-dim); margin: 0; line-height: 1.4; }

.arcade .btax { grid-column: 2 / -1; }
.arcade .trow { display: grid; grid-template-columns: minmax(0,1fr) 90px auto; gap: 10px;
  align-items: center; font-size: ${b(17)}; color: var(--a-ink-dim); padding: 3px 0; }
.arcade .trow .tt { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.arcade .trow .tbar { height: 7px; background: var(--a-line2); border-radius: 999px; overflow: hidden; }
.arcade .trow .tbar i { display: block; height: 100%; background: var(--a-accent); border-radius: 999px;
  transition: width .9s cubic-bezier(.2,.7,.2,1); }
.arcade .trow b { color: var(--a-ink); font-variant-numeric: tabular-nums; font-size: ${b(15)}; }

/* ZONE 3 — two usage lists, side by side. Full width because there are TWO of
   them; one alone would have gone in the right stack. */
.arcade .busage:nth-of-type(odd) { grid-column: 1 / 3; }
.arcade .busage:nth-of-type(even) { grid-column: 3 / -1; }
/* Alone in the row (no MCP data yet) it takes the whole width. */
.arcade .busage.bwide { grid-column: 1 / -1; }
.arcade .urow { display: grid; grid-template-columns: 10px minmax(0,1fr) 84px 72px; gap: 10px;
  align-items: center; padding: 4px 0; font-size: ${b(17)}; }
.arcade .udot { width: 9px; height: 9px; border-radius: 2px; display: block; }
.arcade .uname { color: var(--a-ink); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.arcade .utrack { height: 7px; background: var(--a-line2); border-radius: 999px; overflow: hidden; }
.arcade .utrack i { display: block; height: 100%; border-radius: 999px;
  transition: width .9s cubic-bezier(.2,.7,.2,1); }
.arcade .uval { text-align: right; font-family: var(--a-fd-font); font-size: ${d(11)};
  color: var(--a-ink); font-variant-numeric: tabular-nums; line-height: 1.15; }
.arcade .uval em { display: block; font-style: normal; font-family: var(--a-fb-font);
  font-size: ${b(14)}; color: var(--a-ink-dim); }
.arcade .unote { font-size: ${b(14)}; color: var(--a-ink-dim); line-height: 1.45;
  margin: 10px 0 0; }

.arcade .bhl { background: linear-gradient(150deg, var(--a-accent), color-mix(in srgb, var(--a-accent) 55%, var(--a-bg-solid)));
  border-color: var(--a-accent); color: var(--a-on-accent); }
.arcade .bhl .k, .arcade .bhl .big, .arcade .bhl .bsub { color: var(--a-on-accent); opacity: .95; }
.arcade .bkpi .big, .arcade .bhl .big { font-family: var(--a-fd-font); font-size: ${d(26)};
  color: var(--a-ink); line-height: 1; font-variant-numeric: tabular-nums; }
.arcade .bsub { font-size: ${b(15)}; color: var(--a-ink-dim); margin-top: 8px; line-height: 1.4; }

/* ---------------------------------------------------------------------------
   SKELETON: raid (Raid Boss).
   Every bar reads as health or damage against a shared goal, not a position.
   --------------------------------------------------------------------------- */
.arcade .raid .boss { display: flex; gap: 16px; align-items: center; border: 1px solid var(--a-line);
  border-radius: var(--a-radius); padding: 14px 16px; margin-bottom: 16px;
  background: linear-gradient(120deg, color-mix(in srgb, var(--a-hot) 14%, transparent), transparent 60%), var(--a-panel); }
.arcade .raid .bface { font-size: 46px; line-height: 1; flex: none; }
.arcade .raid .bname { font-family: var(--a-fd-font); font-size: ${d(15)}; color: var(--a-hot);
  text-transform: var(--a-display-transform); letter-spacing: var(--a-display-tracking); }
.arcade .raid .hp { position: relative; height: 26px; margin-top: 10px; border-radius: var(--a-radius-sm);
  background: var(--a-line2); border: 1px solid var(--a-line); overflow: hidden; }
.arcade .raid .hp i { display: block; height: 100%;
  background: linear-gradient(90deg, var(--a-hot), var(--a-gold));
  transition: width 1s cubic-bezier(.2,.7,.2,1); }
.arcade .raid .hplab { position: absolute; inset: 0; display: grid; place-items: center;
  font-family: var(--a-fd-font); font-size: ${d(9)}; color: var(--a-ink);
  text-shadow: 0 1px 2px rgba(0,0,0,.65); font-variant-numeric: tabular-nums; }
.arcade .rbody { display: grid; grid-template-columns: minmax(0,1.25fr) minmax(0,1fr); gap: 14px; align-items: start; }
.arcade .rcard { border: 1px solid var(--a-line); background: var(--a-panel); border-radius: var(--a-radius);
  padding: 14px 16px; min-width: 0; }
.arcade .rcard .k { font-family: var(--a-fd-font); font-size: ${d(9)}; letter-spacing: .1em;
  text-transform: uppercase; color: var(--a-gold); margin-bottom: 10px; }
.arcade .rrow { display: grid; grid-template-columns: 22px auto minmax(0,1fr) 90px auto; gap: 10px;
  align-items: center; padding: 6px 2px; font-size: ${b(19)}; color: var(--a-ink); }
.arcade .rrow.you { background: var(--a-you); border-radius: var(--a-radius-sm);
  box-shadow: inset 3px 0 0 var(--a-accent); }
.arcade .rrow .rk { font-family: var(--a-fd-font); font-size: ${d(10)}; color: var(--a-ink-dim);
  font-variant-numeric: tabular-nums; }
.arcade .rrow .nm { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.arcade .rrow b { font-family: var(--a-fd-font); font-size: ${d(10)}; color: var(--a-hot);
  font-variant-numeric: tabular-nums; }
.arcade .dmg { display: block; height: 8px; border-radius: 999px; background: var(--a-line2); overflow: hidden; }
.arcade .dmg i { display: block; height: 100%; border-radius: 999px;
  background: linear-gradient(90deg, var(--a-hot), var(--a-gold));
  transition: width .9s cubic-bezier(.2,.7,.2,1); }
.arcade .tg { margin-bottom: 10px; }
.arcade .tgl { display: flex; justify-content: space-between; gap: 10px; font-size: ${b(17)};
  color: var(--a-ink-dim); margin-bottom: 5px; }
.arcade .tgl b { color: var(--a-ink); font-variant-numeric: tabular-nums; }
.arcade .rnote { border-top: 1px solid var(--a-line2); padding-top: 10px; margin-top: 12px;
  font-size: ${b(17)}; color: var(--a-ink-dim); line-height: 1.45; }
.arcade .rnote b { color: var(--a-flame); }

/* ---------------------------------------------------------------------------
   SKELETON: flaps (Split-Flap Board) — also the reception screen.
   Tiles are aria-hidden; each row carries a label, so a screen reader hears
   "rank 1, kofi adeyemi, 38.6M" rather than fourteen separate characters.
   --------------------------------------------------------------------------- */
.arcade .flapboard .fl { display: inline-grid; place-items: center; width: 20px; height: 28px;
  font-family: var(--a-fd-font); font-size: ${d(12)}; color: var(--a-ink); position: relative;
  border: 1px solid var(--a-line); border-radius: 2px;
  background: linear-gradient(180deg, var(--a-panel2) 0 49%, var(--a-panel) 51% 100%); }
.arcade .flapboard .fl::after { content: ""; position: absolute; left: 0; right: 0; top: 50%;
  height: 1px; background: var(--a-bg-solid); opacity: .8; }
.arcade .flapboard .fl.acc { color: var(--a-gold); }
.arcade .flapmarquee { display: flex; gap: 3px; margin-bottom: 16px; }
.arcade .flapboard .flgap { width: 12px; }
.arcade .flapboard .brows { border: 1px solid var(--a-line); border-radius: var(--a-radius);
  background: var(--a-board-bg); overflow: hidden; margin-bottom: 16px; }
.arcade .flapboard .brow { display: grid; grid-template-columns: auto auto minmax(0,1fr) auto 120px;
  gap: 14px; align-items: center; padding: 10px 14px; border-bottom: 1px solid var(--a-line2); }
.arcade .flapboard .brow:last-child { border-bottom: 0; }
.arcade .flapboard .brow.hi { background: color-mix(in srgb, var(--a-gold) 8%, transparent); }
.arcade .flapboard .brow.you { box-shadow: inset 3px 0 0 var(--a-accent); background: var(--a-you); }
.arcade .flapboard .brow .nm { font-size: ${b(21)}; color: var(--a-ink); line-height: 1.15;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.arcade .flapboard .brow .sub { font-family: var(--a-fd-font); font-size: ${d(8)}; color: var(--a-ink-faint);
  text-transform: uppercase; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.arcade .flaprank, .arcade .flapscore { display: inline-flex; gap: 3px; }
.arcade .flapboard .status { font-family: var(--a-fd-font); font-size: ${d(8)}; letter-spacing: .08em;
  text-align: right; color: var(--a-ink-faint); }
.arcade .flapboard .status.new { color: var(--a-gold); }
.arcade .flapboard .status.hot { color: var(--a-flame); }
.arcade .flapbottom { display: grid; grid-template-columns: repeat(3,1fr); gap: 12px; }
.arcade .flapbottom .mini { border: 1px solid var(--a-line); background: var(--a-panel);
  border-radius: var(--a-radius); padding: 12px 14px; }
.arcade .flapbottom .k { font-family: var(--a-fd-font); font-size: ${d(9)}; color: var(--a-gold);
  margin-bottom: 8px; text-transform: uppercase; letter-spacing: .1em; }
.arcade .flapbottom .fr { display: flex; justify-content: space-between; gap: 10px; font-size: ${b(17)};
  color: var(--a-ink-dim); padding: 3px 0; }
.arcade .flapbottom .fr b { font-family: var(--a-fd-font); font-size: ${d(9)}; color: var(--a-ink);
  font-variant-numeric: tabular-nums; }

/* ---------------------------------------------------------------------------
   RECEPTION MODE (?tv=1). Not a layout and not an endpoint: a URL parameter
   that puts ANY skeleton into a presentation state — no controls, no
   sign-out, no picker, nothing to interact with — and scales the type with the
   viewport so the board reads from across a room. Split-Flap is the variant it
   is designed around, because a board that turns over when someone's score
   changes is the only one that rewards being watched.
   --------------------------------------------------------------------------- */
.arcade[data-tv="1"] .controls, .arcade[data-tv="1"] .headright,
.arcade[data-tv="1"] .menubtn, .arcade[data-tv="1"] .hint,
.arcade[data-tv="1"] .foot, .arcade[data-tv="1"] .spotlight { display: none; }
.arcade[data-tv="1"] .wrap { max-width: min(2400px, 96vw); padding: 2vh 3vw; }
.arcade[data-tv="1"] { font-size: calc(clamp(20px, 1.35vw, 34px) * var(--a-fs)); }
.arcade[data-tv="1"] h1 { font-size: calc(clamp(30px, 3.4vw, 74px) * var(--a-fd)); }
.arcade[data-tv="1"] .flapboard .fl { width: clamp(20px, 1.7vw, 42px); height: clamp(28px, 2.4vw, 58px);
  font-size: calc(clamp(12px, 1.1vw, 28px) * var(--a-fd)); }
.arcade[data-tv="1"] .flapboard .brow { padding: clamp(10px, 1vw, 22px) clamp(14px, 1.4vw, 30px); }
.arcade[data-tv="1"] .flapboard .brow .nm { font-size: calc(clamp(20px, 1.5vw, 40px) * var(--a-fs)); }
.arcade[data-tv="1"] .row, .arcade[data-tv="1"] .brow, .arcade[data-tv="1"] .rrow { cursor: default; }
.arcade[data-tv="1"] .tvclock { display: block; }
.arcade .tvclock { display: none; font-family: var(--a-fd-font); font-size: ${d(10)};
  color: var(--a-ink-faint); letter-spacing: .1em; }

/* ---------------------------------------------------------------------------
   SKELETON: motd (Retro Terminal).
   A genuinely different tree for the same model — no cards, no grid, no
   podium. Styled here rather than in the theme overlay because a skeleton is
   structure and belongs to every theme that selects it, whereas an overlay is
   one theme's ornament.
   --------------------------------------------------------------------------- */
.arcade .motd { font-family: var(--a-fd-font); font-size: ${d(13)}; line-height: 1.5; color: var(--a-accent); }
.arcade .mline { white-space: pre-wrap; word-break: break-word; }
.arcade .mpr { color: var(--a-cyan); }
.arcade .mdim { color: var(--a-ink-faint); }
.arcade .mwarn { color: var(--a-gold); }
.arcade .mhl { color: var(--a-ink); }
.arcade .mrule { border-top: 1px dashed var(--a-line); margin: 10px 0; }
.arcade .mtable { border-collapse: collapse; width: 100%; font-family: var(--a-fd-font); font-size: ${d(13)}; }
.arcade .mtable th { text-align: left; font-weight: normal; color: var(--a-ink-faint); padding: 2px 14px 6px 0; }
.arcade .mtable td { padding: 3px 14px 3px 0; white-space: nowrap; }
.arcade .mtable th.mr, .arcade .mtable td.mr { text-align: right; padding-right: 0; width: 1%;
  font-variant-numeric: tabular-nums; }
.arcade .mrow { cursor: pointer; }
.arcade .mrow:hover, .arcade .mrow:focus-visible { background: var(--a-row-hover); outline: none; }
.arcade .mrow.myou { background: var(--a-you); }
.arcade .mhist { display: flex; align-items: baseline; gap: 10px; }
.arcade .mtopic { flex: 0 0 auto; min-width: 15ch; color: var(--a-ink-dim); }
.arcade .mbar { flex: 1; overflow: hidden; white-space: nowrap; letter-spacing: -1px; }
.arcade .mcount { flex: none; color: var(--a-gold); font-variant-numeric: tabular-nums; }
.arcade .mcur { display: inline-block; width: 9px; height: ${d(13)}; background: var(--a-accent);
  vertical-align: -2px; animation: abl 1.1s steps(1) infinite; }

/* ---------------------------------------------------------------------------
   PERSON PANEL. Rank in the header, six stats in a rail, one share bar for the
   taxonomy: the drawer used to run past the bottom of a laptop screen.
   --------------------------------------------------------------------------- */
.arcade .phead { display: grid; grid-template-columns: auto minmax(0,1fr) auto; gap: 15px;
  align-items: center; padding-bottom: 15px; border-bottom: 1px solid var(--a-line2); }
.arcade .rankpill { text-align: right; padding-left: 16px; border-left: 1px solid var(--a-line2); }
.arcade .rankpill .rv { font-family: var(--a-fd-font); font-size: ${d(26)}; color: var(--a-accent);
  line-height: 1; font-variant-numeric: tabular-nums; }
.arcade .rankpill .rl { font-family: var(--a-fd-font); font-size: ${d(8)}; letter-spacing: .1em;
  text-transform: uppercase; color: var(--a-ink-faint); margin-top: 3px; }
.arcade .pbody { display: grid; grid-template-columns: minmax(0,1fr) 186px; gap: 22px;
  padding-top: 15px; }
.arcade .prail { min-width: 0; }
.arcade .taxhead { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
.arcade .taxhead .thint { font-family: var(--a-fb-font); font-size: ${b(14)}; letter-spacing: 0;
  text-transform: none; color: var(--a-ink-dim); }
/* Label left, value right, hairline between. Holds all six numbers in the
   height two rows of the old three-across tiles used. */
.arcade .statlist { display: flex; flex-direction: column; margin-top: 8px; }
.arcade .statlist > div { display: flex; align-items: baseline; justify-content: space-between;
  gap: 10px; padding: 7px 0; border-bottom: 1px solid var(--a-line2); }
.arcade .statlist > div:last-child { border-bottom: 0; }
.arcade .statlist span { font-family: var(--a-fd-font); font-size: ${d(8)}; letter-spacing: .1em;
  text-transform: uppercase; color: var(--a-ink-faint); }
.arcade .statlist b { font-family: var(--a-fd-font); font-size: ${d(13)}; color: var(--a-ink);
  font-variant-numeric: tabular-nums; }
/* Tokens and streak are what people compare, so they get the emphasis; the
   other four are reference values. */
.arcade .statlist .primary b { font-size: ${d(16)}; color: var(--a-accent); }
/* The CHAT / CLAUDE CODE rows under Tokens. Indented and dimmed because they
   are a BREAKDOWN of the row above, not two more stats: the reader has to be
   able to see at a glance that these two make up that one rather than adding
   to it. */
.arcade .statlist .sfstat { padding-left: 10px; }
.arcade .statlist .sfstat span, .arcade .statlist .sfstat b { color: var(--a-ink-dim); }
.arcade .statlist .sfstat b { font-size: ${d(10)}; }
.arcade .prail .splitnote { margin-top: 10px; }
/* One segmented share bar plus a legend, instead of a bar per topic. */
.arcade .tshare { margin-top: 8px; }
.arcade .tsbar { display: flex; height: 22px; border-radius: var(--a-radius-sm); overflow: hidden;
  gap: 1.5px; background: var(--a-line2); }
.arcade .tsbar i { display: block; height: 100%; min-width: 2px;
  transition: width .9s cubic-bezier(.2,.7,.2,1); }
.arcade .tslegend { display: grid; grid-template-columns: 1fr 1fr; gap: 3px 18px; margin-top: 10px; }
.arcade .tslegend > div { display: flex; align-items: center; gap: 8px; font-size: ${b(16)};
  color: var(--a-ink-dim); min-width: 0; }
.arcade .tslegend em { width: 8px; height: 8px; border-radius: 2px; flex: none; display: block; }
.arcade .tslegend span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.arcade .tslegend b { margin-left: auto; color: var(--a-ink); font-variant-numeric: tabular-nums;
  font-size: ${b(15)}; }

/* ---------------------------------------------------------------------------
   RIVALRY. Office of Origin and Team Wars.
   The two sides are the palette's own warm and cool accents -- far apart for
   every kind of colour blindness, and plainly not any real team's colours.
   NOTHING here dims, reddens or shrinks the trailing side: only the shield
   moves, so a group can be behind without being humiliated.
   --------------------------------------------------------------------------- */
.arcade .rivalry { --side-a: var(--a-flame); --side-b: var(--a-cyan); }
.arcade .rvhead { display: flex; align-items: flex-end; justify-content: space-between;
  gap: 16px; flex-wrap: wrap; margin-bottom: 14px; }
.arcade .rvtitle { font-family: var(--a-fd-font); font-size: ${d(20)}; color: var(--a-ink); margin: 0; }
.arcade .rvsub { font-size: ${b(17)}; color: var(--a-ink-dim); margin: 3px 0 0; }
.arcade .picker { display: flex; gap: 2px; border: 1px solid var(--a-line); border-radius: 999px;
  padding: 3px; background: var(--a-panel); }
.arcade .picker button { font-family: var(--a-fd-font); font-size: ${d(9)}; letter-spacing: .08em;
  text-transform: uppercase; border: 0; background: transparent; color: var(--a-ink-dim);
  padding: 6px 15px; border-radius: 999px; cursor: pointer; }
.arcade .picker button[data-on="1"] { background: var(--a-accent); color: var(--a-on-accent); }
.arcade .rvpick { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; }
.arcade .rvpick label { display: flex; align-items: center; gap: 8px; font-family: var(--a-fd-font);
  font-size: ${d(8)}; letter-spacing: .1em; text-transform: uppercase; color: var(--a-ink-faint); }
.arcade .rvpick select { font: inherit; font-family: var(--a-fb-font); font-size: ${b(17)};
  letter-spacing: 0; text-transform: none; color: var(--a-ink); background: var(--a-panel);
  border: 1px solid var(--a-line); border-radius: var(--a-radius-sm); padding: 6px 10px; }
.arcade .rvv { font-family: var(--a-fd-font); color: var(--a-ink-faint); font-size: ${d(11)}; }

.arcade .arena { border: 1px solid var(--a-line); background: var(--a-panel);
  border-radius: var(--a-radius); overflow: hidden; }
/* SCOPED TO .arena, and that is a bug fix rather than tidying. This rule and
   the card banner at the bottom of this file share a class name, and this one
   is EARLIER, so its three-column grid leaked into every card banner: the bold
   lead-in became column one, the paragraph column two, and a third child -- a
   code element in the copy -- became a column of its own on the far right. It
   survived unnoticed because the old card banners happened to have exactly two
   children. The arena markup itself was replaced by the league table in
   v0.21.0 and no element carries .arena any more, so this now matches nothing
   at all, which is the correct amount for a dead rule to match. */
.arcade .arena .banner { display: grid; grid-template-columns: 1fr auto 1fr; align-items: stretch;
  border-bottom: 1px solid var(--a-line); }
.arcade .bside { padding: 18px 20px; display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.arcade .bside.a { background: color-mix(in srgb, var(--side-a) 12%, transparent); }
.arcade .bside.b { background: color-mix(in srgb, var(--side-b) 12%, transparent);
  align-items: flex-end; text-align: right; }
.arcade .bpin { font-family: var(--a-fd-font); font-size: ${d(8)}; letter-spacing: .1em;
  text-transform: uppercase; color: var(--a-ink-faint); }
.arcade .bname { font-family: var(--a-fd-font); font-size: ${d(24)}; line-height: 1.05;
  overflow: hidden; text-overflow: ellipsis; }
.arcade .bside.a .bname { color: var(--side-a); }
.arcade .bside.b .bname { color: var(--side-b); }
.arcade .bmeta { font-size: ${b(16)}; color: var(--a-ink-dim); }
.arcade .bcentre { padding: 14px 22px; display: flex; flex-direction: column; align-items: center;
  gap: 7px; background: var(--a-panel2); border-left: 1px solid var(--a-line);
  border-right: 1px solid var(--a-line); }
.arcade .arena .score { font-family: var(--a-fd-font); font-size: ${d(26)}; line-height: 1;
  display: flex; gap: 9px; align-items: baseline; font-variant-numeric: tabular-nums; }
.arcade .arena .score .sa { color: var(--side-a); }
.arcade .arena .score .sb { color: var(--side-b); }
.arcade .arena .score .dash { color: var(--a-ink-faint); }
.arcade .vslab { font-family: var(--a-fd-font); font-size: ${d(8)}; letter-spacing: .12em;
  text-transform: uppercase; color: var(--a-ink-faint); }
.arcade .pips { display: flex; gap: 5px; }
.arcade .pips i { width: 10px; height: 10px; border-radius: 2px; display: block;
  border: 1.5px solid var(--a-line); }
.arcade .pips i[data-side="a"] { background: var(--side-a); border-color: var(--side-a); }
.arcade .pips i[data-side="b"] { background: var(--side-b); border-color: var(--side-b); }

.arcade .mirror { padding: 16px 20px; display: flex; flex-direction: column; gap: 12px; }
.arcade .mrow { display: grid; grid-template-columns: 64px 1fr 140px 1fr 64px; align-items: center;
  gap: 12px; }
.arcade .mval { font-family: var(--a-fd-font); font-size: ${d(11)}; font-variant-numeric: tabular-nums; }
.arcade .mval.a { text-align: right; color: var(--side-a); }
.arcade .mval.b { color: var(--side-b); }
.arcade .mtrack { height: 11px; background: var(--a-line2); border-radius: 3px; overflow: hidden;
  position: relative; display: block; }
.arcade .mtrack i { position: absolute; top: 0; bottom: 0; display: block; border-radius: 3px;
  transition: width .9s cubic-bezier(.2,.7,.2,1); }
.arcade .mtrack.l i { right: 0; background: var(--side-a); }
.arcade .mtrack.r i { left: 0; background: var(--side-b); }
.arcade .mlab { text-align: center; font-family: var(--a-fd-font); font-size: ${d(8)};
  letter-spacing: .1em; text-transform: uppercase; color: var(--a-ink-faint); }
.arcade .mlab em { display: block; font-style: normal; font-family: var(--a-fb-font);
  font-size: ${b(15)}; letter-spacing: 0; text-transform: none; color: var(--a-ink-dim);
  margin-top: 2px; }

.arcade .shield { padding: 12px 20px; border-top: 1px solid var(--a-line);
  background: var(--a-panel2); font-size: ${b(17)}; color: var(--a-ink-dim); margin: 0; }
.arcade .pooled { padding: 11px 20px; border-top: 1px solid var(--a-line); margin: 0;
  font-size: ${b(16)}; color: var(--a-ink-dim); display: flex; gap: 10px; align-items: center;
  flex-wrap: wrap; }
.arcade .greychip { font-family: var(--a-fd-font); font-size: ${d(8)}; letter-spacing: .07em;
  border: 1px dashed var(--a-line); border-radius: 999px; padding: 4px 10px;
  color: var(--a-ink-faint); flex: none; }
.arcade .rvfoot { padding: 11px 20px; border-top: 1px solid var(--a-line); margin: 0;
  font-size: ${b(15)}; color: var(--a-ink-dim); line-height: 1.5; }

.arcade .standings { display: grid; grid-template-columns: repeat(auto-fit,minmax(160px,1fr));
  gap: 10px; margin-top: 12px; }
.arcade .ocard { border: 1px solid var(--a-line); background: var(--a-panel);
  border-radius: var(--a-radius-sm); padding: 12px 14px; }
/* A pooled group is drawn quieter, never redder: it is not losing, it is just
   too small to field a side this window. */
.arcade .ocard[data-eligible="0"] { border-style: dashed; }
.arcade .ocard .on { font-size: ${b(17)}; color: var(--a-ink); margin-bottom: 3px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.arcade .ocard .ov { font-family: var(--a-fd-font); font-size: ${d(16)}; color: var(--a-ink);
  font-variant-numeric: tabular-nums; }
.arcade .ocard .os { font-size: ${b(14)}; color: var(--a-ink-dim); }

.arcade .usagegrid { display: grid; grid-template-columns: repeat(auto-fit,minmax(320px,1fr));
  gap: 12px; }

/* ---------------------------------------------------------------------------
   CHARTS. One block for every mark on the board, so the four reworked views
   cannot drift apart on what an axis label or a legend looks like.

   TEXT WEARS TEXT TOKENS, never a series colour: values, categories, legends
   and axis ticks are --a-ink*, and identity comes from the coloured mark
   BESIDE them. A light categorical hue is illegible as text on either surface,
   which is the reason this is a rule rather than a preference.
   --------------------------------------------------------------------------- */
.arcade .card { border: 1px solid var(--a-line); background: var(--a-panel);
  border-radius: var(--a-radius); padding: 18px; margin: 14px 0; }
.arcade .cardhead { display: flex; justify-content: space-between; align-items: flex-start;
  gap: 16px; flex-wrap: wrap; margin-bottom: 14px; }
.arcade .ctitle { font-family: var(--a-fd-font); font-size: ${d(11)}; color: var(--a-ink);
  letter-spacing: var(--a-display-tracking); text-transform: uppercase; }
.arcade .csub { font-size: ${b(14)}; color: var(--a-ink-dim); max-width: 70ch; margin-top: 6px; }
.arcade .cnote { font-size: ${b(13)}; color: var(--a-ink-faint); margin-top: 12px;
  border-top: 1px solid var(--a-line); padding-top: 10px; }
.arcade .cnote b { color: var(--a-ink); }
/* Capped at the natural width every chart is drawn at (see NATURAL_W in
   arcade/charts.tsx). Without the cap an SVG in a wide card scales its own
   text up and two charts on one page disagree about what 13px looks like. */
.arcade .plot { display: block; width: 100%; max-width: 560px; height: auto; overflow: visible; }
.arcade .plot.wide { max-width: 1000px; }
.arcade .chcat { fill: var(--a-ink-dim); font-size: 13px; font-family: var(--a-fb-font); }
.arcade .chval { fill: var(--a-ink); font-size: 13px; font-family: var(--a-fd-font);
  font-variant-numeric: tabular-nums; }
.arcade .chnote { fill: var(--a-ink-faint); }
.arcade .chaxis { fill: var(--a-ink-faint); font-size: 11px; font-variant-numeric: tabular-nums; }
.arcade .chend { fill: var(--a-ink-dim); font-size: 12px; }
.arcade .chmark { fill: var(--a-ink-faint); font-size: 11px; }
.arcade .chhero { fill: var(--a-ink); font-size: 34px; font-family: var(--a-fd-font); }
.arcade .chherol { fill: var(--a-ink-faint); font-size: 11px; }
.arcade .chlegend { display: flex; flex-wrap: wrap; gap: 6px 18px; margin-top: 12px; }
.arcade .chlegend div { display: flex; align-items: center; gap: 7px; font-size: ${b(13)};
  color: var(--a-ink-dim); }
.arcade .chlegend i { width: 12px; height: 12px; border-radius: 3px; display: inline-block; flex: none; }
.arcade .chlegend i[data-shape="line"] { width: 16px; height: 3px; border-radius: 2px; }
.arcade .chwrap { position: relative; }
.arcade .chtip { position: absolute; top: 8px; right: 8px; pointer-events: none;
  background: var(--a-panel2); border: 1px solid var(--a-line); border-radius: var(--a-radius-sm);
  padding: 8px 10px; font-size: ${b(13)}; min-width: 150px; box-shadow: var(--a-shadow); }
.arcade .chtip b { display: block; color: var(--a-ink-dim); font-size: ${b(12)}; margin-bottom: 4px; }
.arcade .chtip div { display: flex; align-items: center; gap: 7px; }
.arcade .chtip i { width: 14px; height: 3px; border-radius: 2px; flex: none; }
/* Values lead, labels follow: the number is the strong element here, the
   inverse of the legend's hierarchy, because the reader already has the
   series and wants the figure. */
.arcade .chtip em { font-style: normal; color: var(--a-ink); font-variant-numeric: tabular-nums; }
.arcade .chtip span { color: var(--a-ink-faint); }
.arcade .chsm { display: block; width: 100%; height: 40px; }
/* Heatmap cell values. Smaller than .chval and explicitly fill-less: the
   colour of a printed value is chosen per cell from the cell's own luminance,
   so a stylesheet must not set it. */
.arcade .chcell { font-size: 10px; font-family: var(--a-fd-font);
  font-variant-numeric: tabular-nums; }
/* The sequential legend. The two end labels carry the unit; the swatches are
   the ramp itself, in order, with no gap so the scale reads as continuous. */
.arcade .heatkey { display: flex; align-items: center; gap: 8px; margin-top: 12px;
  font-size: ${b(12)}; color: var(--a-ink-faint); font-variant-numeric: tabular-nums; }
.arcade .heatkey i { width: 26px; height: 10px; display: inline-block; flex: none; }
.arcade .heatkey i + i { margin-left: -8px; }
/* The timeline under the race track. One row: transport, the scrubber, the
   day it is showing. */
.arcade .scrub { display: flex; align-items: center; gap: 12px; margin: 14px 0 4px;
  flex-wrap: wrap; }
.arcade .scrub input[type="range"] { flex: 1 1 220px; min-width: 180px; accent-color: var(--a-accent); }
.arcade .scrub .day { font-family: var(--a-fd-font); font-size: ${d(11)}; color: var(--a-ink);
  min-width: 92px; font-variant-numeric: tabular-nums; }
.arcade .scrub button { font-family: var(--a-fd-font); font-size: ${d(10)};
  text-transform: uppercase; letter-spacing: var(--a-display-tracking);
  background: var(--a-panel2); color: var(--a-ink-dim); border: 1px solid var(--a-line);
  border-radius: var(--a-radius-sm); padding: 6px 12px; cursor: pointer; }
.arcade .scrub button:hover { color: var(--a-ink); border-color: var(--a-line2); }
/* Two pickers side by side on the head-to-head tab. */
.arcade .fixpick { display: grid; grid-template-columns: repeat(auto-fit,minmax(220px,1fr));
  gap: 14px; align-items: end; margin-bottom: 4px; }
.arcade .fixpick label { display: block; font-family: var(--a-fd-font); font-size: ${d(9)};
  color: var(--a-ink-faint); text-transform: uppercase;
  letter-spacing: var(--a-display-tracking); margin-bottom: 6px; }
.arcade .fixpick select { width: 100%; background: var(--a-panel2); color: var(--a-ink);
  border: 1px solid var(--a-line); border-radius: var(--a-radius-sm); padding: 9px 10px;
  font-size: ${b(14)}; font-family: var(--a-fb-font); }
.arcade .fixpick select:focus-visible { outline: 2px solid var(--a-accent); outline-offset: 2px; }
/* The trajectory phrase in the wide board. Regular weight and dim ink on
   purpose: it is a caption on a sparkline, never a verdict with emphasis. */
.arcade table.chtable td.traj { color: var(--a-ink-dim); font-size: ${b(12)}; max-width: 210px; }
.arcade .split { display: grid; grid-template-columns: repeat(auto-fit,minmax(300px,1fr)); gap: 22px; }
.arcade .kpis { display: grid; grid-template-columns: repeat(auto-fit,minmax(180px,1fr)); gap: 10px;
  margin: 14px 0; }
.arcade .kpi { border: 1px solid var(--a-line); background: var(--a-panel); padding: 12px 14px;
  border-radius: var(--a-radius-sm); }
.arcade .kpi .k { font-family: var(--a-fd-font); font-size: ${d(9)}; color: var(--a-ink-faint);
  text-transform: uppercase; letter-spacing: var(--a-display-tracking); }
.arcade .kpi .v { font-family: var(--a-fd-font); font-size: ${d(17)}; color: var(--a-ink); margin: 6px 0 2px;
  font-variant-numeric: tabular-nums; }
.arcade .kpi .d { font-size: ${b(13)}; color: var(--a-ink-dim); }
/* A table view exists beside every chart, so nothing is gated behind hover. */
.arcade .tablewrap { overflow-x: auto; scrollbar-width: thin; }
.arcade table.chtable { width: 100%; border-collapse: collapse; font-size: ${b(13)}; min-width: 620px; }
/* A table inside a half-column carries no minimum: forcing 620px there
   produces a scrollbar across a five-row table, which reads as a defect. */
.arcade table.chtable.narrow { min-width: 0; }
/* The enriched board. Fifteen columns including a sparkline and a trajectory
   phrase; it scrolls inside .tablewrap rather than crushing, and the page body
   still never scrolls sideways. */
.arcade table.chtable.wide { min-width: 1180px; }
.arcade table.chtable th { text-align: left; font-family: var(--a-fd-font); font-size: ${d(9)};
  color: var(--a-ink-faint); text-transform: uppercase; letter-spacing: var(--a-display-tracking);
  border-bottom: 1px solid var(--a-line); padding: 8px 12px 8px 0; white-space: nowrap; }
.arcade table.chtable td { padding: 9px 12px 9px 0; border-bottom: 1px solid var(--a-line);
  color: var(--a-ink-dim); }
.arcade table.chtable td.num, .arcade table.chtable th.num { text-align: right;
  font-variant-numeric: tabular-nums; }
.arcade table.chtable td b { color: var(--a-ink); font-weight: 600; }
.arcade table.chtable tr[data-thin="1"] td { color: var(--a-ink-faint); }
.arcade .chips { display: flex; flex-wrap: wrap; gap: 8px; }
.arcade .selchip { border: 1px solid var(--a-line); background: transparent; color: var(--a-ink-dim);
  font-family: var(--a-fb-font); font-size: ${b(13)}; padding: 6px 11px; border-radius: 999px;
  cursor: pointer; min-height: 32px; }
.arcade .selchip[data-on="1"] { border-color: var(--a-accent); color: var(--a-ink);
  background: var(--a-hover); }
.arcade .selchip[data-thin="1"] { border-style: dashed; }
.arcade .selchip em { font-style: normal; color: var(--a-ink-faint); margin-left: 6px; }
.arcade .metricpick { display: flex; flex-wrap: wrap; gap: 6px; }
.arcade .metricpick button { border: 1px solid var(--a-line); background: transparent;
  color: var(--a-ink-dim); font-family: var(--a-fd-font); font-size: ${d(9)}; padding: 6px 9px;
  cursor: pointer; text-transform: uppercase; letter-spacing: var(--a-display-tracking); }
.arcade .metricpick button[data-on="1"] { border-color: var(--a-accent); color: var(--a-ink);
  background: var(--a-hover); }
.arcade .banner { border: 1px solid var(--a-line); border-left: 3px solid var(--a-accent);
  background: var(--a-panel); padding: 12px 14px; font-size: ${b(14)}; color: var(--a-ink-dim);
  margin: 14px 0; border-radius: var(--a-radius-sm); }
/* The lead-in is always a whole line: inline, it wrapped mid-phrase and the
   paragraph that followed read as an indent. */
.arcade .banner b { color: var(--a-ink); display: block; }
.arcade .method { display: grid; grid-template-columns: repeat(auto-fit,minmax(300px,1fr)); gap: 22px; }
.arcade .method table { width: 100%; border-collapse: collapse; font-size: ${b(13)}; }
.arcade .method td { padding: 7px 10px 7px 0; border-bottom: 1px solid var(--a-line);
  vertical-align: top; color: var(--a-ink-dim); }
.arcade .method td:first-child { color: var(--a-ink-faint); width: 34%; }
.arcade .method ul { margin: 0; padding-left: 20px; color: var(--a-ink-dim); }
.arcade .method li { margin-bottom: 9px; }
.arcade .method li b { color: var(--a-ink); }
.arcade .dh { font-family: var(--a-fd-font); font-size: ${d(9)}; color: var(--a-ink-faint);
  text-transform: uppercase; letter-spacing: var(--a-display-tracking); margin: 0 0 10px; }
.arcade .smgrid { display: grid; grid-template-columns: repeat(auto-fit,minmax(190px,1fr)); gap: 12px; }
.arcade .smcard { border: 1px solid var(--a-line); border-radius: var(--a-radius-sm); padding: 11px 12px;
  cursor: pointer; background: transparent; text-align: left; width: 100%; }
.arcade .smcard:hover { background: var(--a-hover); }
.arcade .smcard .smn { font-size: ${b(14)}; color: var(--a-ink); display: flex;
  justify-content: space-between; gap: 8px; }
.arcade .smcard .sms { font-size: ${b(12)}; color: var(--a-ink-faint); margin-top: 2px; }
.arcade .status { display: inline-flex; align-items: center; gap: 6px; }
.arcade .status i { width: 9px; height: 9px; border-radius: 50%; display: inline-block; flex: none; }
/* Build log. A date rail and a body, which collapses to one column on a
   phone: the date above the entry reads fine stacked, and a 90px rail beside
   a two-line paragraph does not. */
.arcade .bllist { display: flex; flex-direction: column; }
.arcade .blrow { display: grid; grid-template-columns: 108px minmax(0,1fr); gap: 16px;
  padding: 13px 0; border-bottom: 1px solid var(--a-line); }
.arcade .blrow:last-child { border-bottom: 0; padding-bottom: 0; }
.arcade .bldate { font-family: var(--a-fd-font); font-size: ${d(10)}; color: var(--a-ink-faint);
  text-transform: uppercase; letter-spacing: var(--a-display-tracking); padding-top: 2px; }
.arcade .bltitle { font-size: ${b(14)}; color: var(--a-ink); display: flex; flex-wrap: wrap;
  align-items: center; gap: 8px; }
.arcade .bldetail { font-size: ${b(13)}; color: var(--a-ink-dim); margin-top: 3px; max-width: 62ch; }
.arcade .blchip { font-family: var(--a-fd-font); font-size: ${d(9)}; text-transform: uppercase;
  letter-spacing: var(--a-display-tracking); border: 1px solid var(--a-line); border-radius: 999px;
  padding: 2px 8px; color: var(--a-ink-faint); }
/* One accent, on the newest surface a reader is likeliest to be looking for.
   Colouring all six would make the chips decoration rather than signal. */
.arcade .blchip.bl-arcade { border-color: var(--a-accent); color: var(--a-accent); }
.arcade .blkind { font-family: var(--a-fd-font); font-size: ${d(9)}; color: var(--a-ink-faint);
  text-transform: uppercase; letter-spacing: var(--a-display-tracking); }
@media (max-width: 640px) {
  .arcade .blrow { grid-template-columns: 1fr; gap: 4px; }
}
.arcade .sharegrid { display: grid; grid-template-columns: 320px minmax(0,1fr); gap: 26px;
  align-items: center; }
.arcade .donutwrap { position: relative; }

/* ---------------------------------------------------------------------------
   RESPONSIVE. Four breakpoints, defined once here and inherited by every
   skeleton, because mobile is one shell plus a short reflow rule per skeleton
   rather than eight separate designs.
     1180  the award rail narrows
      880  two-column layouts collapse; the podium returns to source order
      640  the mobile shell: sticky header, sheet menu, mode rail, award
           carousel, fixed "you" bar
      420  badges and flap tiles reduce
   --------------------------------------------------------------------------- */
@media (max-width: 1180px) {
  .arcade .layout { grid-template-columns: minmax(0,1fr) 250px; }
  .arcade .ladder { grid-template-columns: minmax(0,1fr) 250px; }
  /* Two columns: the board keeps the left, everything else stacks in the
     right. The strip halves to 3+2 rather than squeezing five tiles. */
  .arcade .bento { grid-template-columns: minmax(0,1.4fr) minmax(0,1fr); }
  .arcade .bstrip { grid-template-columns: repeat(3, minmax(0,1fr)); }
  .arcade .bdev, .arcade .btax { grid-column: 2 / -1; }
  .arcade .busage:nth-of-type(odd),
  .arcade .busage:nth-of-type(even) { grid-column: 1 / -1; }
}
@media (max-width: 880px) {
  /* Charts reflow with the rest of the shell rather than earning a
     breakpoint of their own. Wide content scrolls inside its own container
     (.tablewrap); the page body never scrolls sideways. */
  .arcade .sharegrid { grid-template-columns: 1fr; }
  .arcade .method td:first-child { width: 40%; }
  .arcade .bento { grid-template-columns: 1fr; }
  /* One column: every span collapses, or a card claims a track that is not
     there and the grid grows a phantom second column. */
  .arcade .bstrip { grid-template-columns: repeat(2, minmax(0,1fr)); }
  .arcade .bdev, .arcade .btax, .arcade .bstrip,
  .arcade .busage:nth-of-type(odd),
  .arcade .busage:nth-of-type(even) { grid-column: 1 / -1; }
  .arcade .urow { grid-template-columns: 10px minmax(0,1fr) 56px 64px; }
  /* No nested scroller on a phone: a card that scrolls inside a page that
     scrolls is a trap for a thumb. The list runs to full length instead. */
  .arcade .blead { grid-row: auto; max-height: none; }
  .arcade .blist { overflow-y: visible; }
  .arcade .rbody { grid-template-columns: 1fr; }
  .arcade .flapbottom { grid-template-columns: 1fr; }
  /* Status is tenure/streak information, not decoration, so it wraps onto its
     own line rather than being dropped. */
  .arcade .flapboard .brow { grid-template-columns: auto auto minmax(0,1fr) auto; gap: 6px 12px; }
  .arcade .flapboard .status { grid-column: 1 / -1; text-align: right; }
  .arcade .layout { grid-template-columns: 1fr; }
  /* Ladder: your own division stays first; the side rail drops below. */
  .arcade .ladder { grid-template-columns: 1fr; }
  /* Person panel: the stat rail drops under the heartbeat rather than being
     squeezed to an unreadable column. */
  .arcade .pbody { grid-template-columns: 1fr; gap: 16px; }
  .arcade .phead { grid-template-columns: auto minmax(0,1fr); }
  .arcade .rankpill { grid-column: 1 / -1; text-align: left; padding-left: 0;
    border-left: 0; border-top: 1px solid var(--a-line2); padding-top: 10px; }
  /* Rivalry: the mirrored rows stack. A centred label between two bars needs
     the width to read; below it, a label above its own pair reads better. */
  .arcade .banner { grid-template-columns: 1fr; }
  .arcade .bside.b { align-items: flex-start; text-align: left; }
  .arcade .bcentre { border-left: 0; border-right: 0;
    border-top: 1px solid var(--a-line); border-bottom: 1px solid var(--a-line); }
  .arcade .mrow { grid-template-columns: 48px 1fr 48px; grid-template-areas:
    "lab lab lab" "av la lb" "bv rb rb"; gap: 6px 10px; }
  .arcade .mlab { grid-area: lab; text-align: left; }
  .arcade .mval.a { grid-area: av; text-align: left; }
  .arcade .mtrack.l { grid-area: la; }
  .arcade .mtrack.l i { right: auto; left: 0; }
  .arcade .mval.b { grid-area: bv; }
  .arcade .mtrack.r { grid-area: rb; }
  .arcade .tslegend { grid-template-columns: 1fr; }
  .arcade .ladderside { position: static; }
  .arcade .gp .pits { grid-template-columns: 1fr; }
  .arcade .row { grid-template-columns: 40px minmax(0,1fr) 104px; }
  .arcade .row .clsC, .arcade .row .lvl, .arcade .row.head .hc, .arcade .row.head .hl { display: none; }
  /* Source order, not the desktop 2/1/3: stacked, the champion goes first. */
  .arcade .podium { grid-template-columns: 1fr; }
  .arcade .plinth { order: 0; }
  .arcade .pstats { grid-template-columns: repeat(2,1fr); }
  .arcade .awards { position: static; }
  .arcade .mtable td, .arcade .mtable th { padding-right: 10px; }
}
@media (max-width: 640px) {
  .arcade .wrap { padding: 0 14px 96px; }

  /* Sticky compact header. Recovers the ~180px of chrome that used to push
     the first ranked row below a second fold. */
  .arcade header.top { position: sticky; top: 0; z-index: 7; margin: 0 -14px; padding: 8px 14px;
    background: var(--a-bg-solid); border-bottom: 1px solid var(--a-line);
    align-items: center; flex-wrap: nowrap; gap: 10px; }
  .arcade header.top .eyebrow, .arcade header.top .coin { display: none; }
  .arcade .brand { flex-direction: row; align-items: center; gap: 10px; }
  .arcade .brandrow { gap: 10px; }
  .arcade .pixlogo { width: 28px; height: 28px; }
  .arcade h1 { font-size: calc(clamp(14px,4.4vw,19px) * var(--a-fd)); margin: 0; }
  .arcade .menubtn { display: inline-flex; flex: none; }
  .arcade .headright { position: static; }
  /* The same .topright node, presented as a sheet. */
  .arcade .topright[data-open="0"] { display: none; }
  .arcade .topright[data-open="1"] { position: absolute; top: 100%; left: 0; right: 0; z-index: 8;
    flex-direction: column; align-items: stretch; justify-content: flex-start;
    background: var(--a-panel); border-bottom: 1px solid var(--a-line); padding: 14px; gap: 12px;
    box-shadow: var(--a-shadow); }
  .arcade .topright .glink { min-height: 44px; display: flex; align-items: center; justify-content: center; }
  .arcade .topright .credits { text-align: left; }

  /* Modes become a horizontal snap rail instead of wrapping onto three lines. */
  .arcade .controls { flex-direction: column; align-items: stretch; gap: 10px; margin: 14px 0 6px; }
  .arcade .modes { flex-wrap: nowrap; overflow-x: auto; scroll-snap-type: x mandatory;
    margin: 0 -14px; padding: 0 14px 2px; scrollbar-width: none; }
  .arcade .modes::-webkit-scrollbar { display: none; }
  .arcade .tab { flex: none; scroll-snap-align: start; min-height: 44px; }
  .arcade .win { min-height: 44px; min-width: 54px; }

  /* The award rail becomes a carousel ABOVE the board. It used to sit below
     all 142 rows, still carrying a sticky that does nothing in one column, so
     nobody on a phone had ever seen it. */
  .arcade .awards { order: -1; flex-direction: row; overflow-x: auto; scroll-snap-type: x mandatory;
    margin: 0 -14px 6px; padding: 0 14px 6px; scrollbar-width: none; }
  .arcade .awards::-webkit-scrollbar { display: none; }
  .arcade .acard { flex: 0 0 76%; scroll-snap-align: start; }

  .arcade .row { grid-template-columns: 30px minmax(0,1fr) auto; gap: 6px 10px; padding: 12px; }
  .arcade .members { grid-template-columns: 1fr; }
  .arcade .modal { padding: 18px 16px 22px; }
  .arcade .modalveil { padding: 20px 10px; }

  /* The "you" anchor becomes a fixed bottom bar. Same node as the desktop
     banner — only its presentation changes. */
  .arcade .spotlight { position: fixed; left: 0; right: 0; bottom: 0; z-index: 9; margin: 0;
    border: 0; border-top: 2px solid var(--a-accent); border-radius: 0;
    background: var(--a-bg-solid); display: flex; align-items: center; gap: 10px;
    padding: 12px 14px calc(12px + env(safe-area-inset-bottom)); box-shadow: var(--a-shadow); }
  .arcade .spotlight .youchase { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis;
    white-space: nowrap; color: var(--a-ink-dim); }
  .arcade .spotlight .youscore { flex: none; font-family: var(--a-fd-font); font-size: ${d(12)};
    color: var(--a-cyan); }

  /* Lanes keep full width and lose the position label; the car chip carries
     the name only, so a 44px lane stays readable at 390px. */
  .arcade .gp .lane { height: 40px; }
  .arcade .gp .pos { display: none; }
  .arcade .gp .car { max-width: calc(100% - 12px); }
  .arcade .gp .cn { font-size: ${b(16)}; }
  .arcade .ladder .lrow { padding: 10px 4px; }

  .arcade .motd { font-size: ${d(11)}; }
  .arcade .mtable { font-size: ${d(11)}; }
  /* Columns drop in priority order so the terminal narrows rather than
     scrolling sideways. */
  .arcade .mtable th:nth-child(3), .arcade .mtable td:nth-child(3),
  .arcade .mtable th:nth-child(4), .arcade .mtable td:nth-child(4) { display: none; }
  .arcade .mtopic { min-width: 11ch; }
}
@media (max-width: 420px) {
  /* Keep three badges plus the streak flame; the rest is noise at this width. */
  .arcade .badges .bdg:nth-child(n+4) { display: none; }
  .arcade .acard { flex: 0 0 86%; }
  /* Flap tiles reduce: rank and score keep their tiles, the marquee does not. */
  .arcade .flapmarquee { display: none; }
  .arcade .flapboard .fl { width: 16px; height: 23px; font-size: ${d(10)}; }
  .arcade .trow { grid-template-columns: minmax(0,1fr) 56px auto; }
  .arcade .mtable th:nth-child(6), .arcade .mtable td:nth-child(6) { display: none; }
}
/* Reduced motion, applied to EVERY theme and EVERY skeleton because it is
   generated from one source. The rain canvas is stopped in JS (a canvas cannot
   be paused by CSS) via the live \`usePrefersReducedMotion\` subscription;
   everything else that moves is stopped here — the blink, the terminal cursor,
   the podium idles, the bar-width transitions, and any marquee an overlay
   introduces.
   Every \`@keyframes\` this sheet defines must be named in the explicit list
   below AND covered by the blanket rule; \`test/arcadeThemes.test.tsx\` asserts
   that mechanically, so adding an animation without a guard fails the build
   rather than shipping a variant that ignores the setting. */
@media (prefers-reduced-motion: reduce) {
  .arcade .blink, .arcade .mcur,
  .arcade .plinth .podav-1, .arcade .plinth .podav-2, .arcade .plinth .podav-3 { animation: none; }
  .arcade .lvl .fill, .arcade .team .fill { transition: none; }
  .arcade .modes, .arcade .awards { scroll-behavior: auto; }
  .arcade *, .arcade *::before, .arcade *::after { animation-duration: 0.001ms !important; animation-iteration-count: 1 !important; }
}
${t.overlay}
`
}
